// src/modules/workspace/ReportComposer.jsx
// ─────────────────────────────────────────────────────────────────────────────
// Report tab: sections, floating ✎ ADD NOTE button → popup modal,
// notes become draggable blocks anywhere in report (no separate Notes tab),
// no Custom section (notes serve that), no numbered headings,
// drag-to-reorder all sections, Dashboard Charts embedded with layout picker.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { C, PALETTE, inferTypes, colStats, pearson, getHistogram, getValueCounts, MetricTile, ChartTip, usePaletteVersion } from './wsShared';
import { EChart } from './DashboardEngine';
import { RenderChart, AnnotationDisplay, AnnotationModal, downloadChartPNG, computeAggrData, buildAutoCharts } from './DashboardEngine';

// ── Section registry ──────────────────────────────────────────────────────────
const FIXED_SECS = [
  { id:'data',         label:'Data',                required:false },
  { id:'overview',     label:'Dataset Overview',   required:true  },
  { id:'stats',        label:'Statistics',         required:false },
  { id:'correlations', label:'Key Correlations',   required:false },
  { id:'distributions',label:'Data Distributions', required:false },
  { id:'visualization',label:'Visualizations',     required:false },
  { id:'health',       label:'Data Health',        required:false },
  { id:'quality',      label:'Data Quality',       required:false },
  { id:'conclusion',   label:'Conclusion',         required:true  },
];

const DISTRIBUTION_CHART_TYPES = new Set(['hist', 'donut', 'pie', 'hbar', 'radial', 'num_overview', 'boxplot']);

function isDistributionChartType(type) {
  return DISTRIBUTION_CHART_TYPES.has(String(type || '').trim());
}

function inferPinnedReportSection(card) {
  const explicit = card?.chart?.reportSection || card?.reportSection || '';
  if (explicit === 'distributions' || explicit === 'visualization') return explicit;

  const rawId = card?.chart?.id;
  const chartId = rawId == null ? '' : String(rawId);
  if (chartId.startsWith('dist_') || chartId.startsWith('auto_dist_')) return 'distributions';
  if (typeof rawId === 'number' || chartId.startsWith('custom_')) return 'visualization';
  return '';
}

function reportChartKey(chart) {
  const cols = Array.isArray(chart?.cols) ? chart.cols.join(',') : '';
  return [
    chart?.type || '',
    chart?.col || '',
    chart?.xCol || '',
    chart?.yCol || '',
    chart?.catCol || '',
    chart?.numCol || '',
    chart?.dateCol || '',
    chart?.valueCol || '',
    chart?.sizeCol || '',
    cols,
  ].join('|');
}

function normalizeReportComposerState(reportState, ds) {
  const allIds = FIXED_SECS.map(s => s.id);
  const requiredIds = FIXED_SECS.filter(s => s.required).map(s => s.id);
  const requestedSections = Array.isArray(reportState?.sections) ? reportState.sections : allIds;
  const sections = [...new Set([...requestedSections, ...requiredIds])].filter(id => allIds.includes(id));

  const requestedOrder = Array.isArray(reportState?.secOrder) ? reportState.secOrder : allIds;
  const secOrder = [...new Set([...requestedOrder, ...allIds])].filter(id => allIds.includes(id));

  const notes = Array.isArray(reportState?.notes)
    ? reportState.notes
        .filter(note => note && typeof note === 'object' && String(note.text || '').trim())
        .slice(0, 30)
        .map(note => ({
          id: String(note.id || `note_${Date.now()}`),
          text: String(note.text || '').trim(),
          tag: String(note.tag || 'observation'),
          ts: note.ts || new Date().toISOString(),
          anchorSection: allIds.includes(note.anchorSection) ? note.anchorSection : null,
        }))
    : [];

  const hiddenChartIds = Array.isArray(reportState?.hiddenChartIds)
    ? [...new Set(reportState.hiddenChartIds.map(id => String(id || '')).filter(Boolean))]
    : [];

  return {
    sections,
    secOrder,
    notes,
    hiddenChartIds,
    title: typeof reportState?.title === 'string' && reportState.title.trim()
      ? reportState.title.trim()
      : `Dataset Analysis Report : ${ds?.name || 'Unknown'}`,
  };
}

// ── Note tag palette ──────────────────────────────────────────────────────────
const NOTE_TAGS = [
  {id:'observation', label:'OBSERVATION', color:C.cyan  },
  {id:'hypothesis',  label:'HYPOTHESIS',  color:C.violet},
  {id:'finding',     label:'FINDING',     color:C.green },
  {id:'todo',        label:'TODO',        color:C.gold  },
  {id:'warning',     label:'WARNING',     color:C.red   },
];
const tagMeta = id => NOTE_TAGS.find(t=>t.id===id)||NOTE_TAGS[0];

function mdValue(value) {
  if (value == null || value === '') return '-';
  const text = Array.isArray(value) ? value.join(', ') : String(value);
  return text.replace(/\|/g, '\\|').replace(/\n/g, ' ').trim() || '-';
}

function mdNumber(value, digits = 3) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  if (Number.isInteger(num)) return String(num);
  return num.toFixed(digits).replace(/\.?0+$/, '');
}

function mdTable(headers, rows) {
  if (!rows?.length) return '';
  return `| ${headers.map(mdValue).join(' | ')} |\n| ${headers.map(() => '---').join(' | ')} |\n${rows.map(row => `| ${row.map(mdValue).join(' | ')} |`).join('\n')}\n\n`;
}

function mdChartFields(chart) {
  const fields = [
    chart?.col,
    chart?.xCol,
    chart?.yCol,
    chart?.catCol,
    chart?.numCol,
    chart?.dateCol,
    chart?.valueCol,
    chart?.sizeCol,
    ...(Array.isArray(chart?.cols) ? chart.cols : []),
  ].filter(Boolean);
  return [...new Set(fields)].join(', ') || '-';
}

function mdChartType(type) {
  return String(type || 'chart').replace(/_/g, ' ').toUpperCase();
}

function mdCorrelationStrength(value) {
  const abs = Math.abs(Number(value) || 0);
  if (abs >= 0.7) return 'strong';
  if (abs >= 0.4) return 'moderate';
  return 'weak';
}

function mdSubheading(title) {
  return `### ${title}\n\n`;
}

function mdMiniHeading(title) {
  return `#### ${title}\n\n`;
}

// ── Note add modal ─────────────────────────────────────────────────────────────
function NoteModal({ onSave, onClose }) {
  const [text, setText] = useState('');
  const [tag,  setTag]  = useState('observation');
  const tm = tagMeta(tag);
  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()}
      style={{position:'fixed',inset:0,background:'rgba(2,5,8,.88)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:C.surface,border:`1px solid ${C.b2}`,borderRadius:6,width:480,maxWidth:'96vw',overflow:'hidden',boxShadow:'0 28px 70px rgba(0,0,0,.8)'}}>
        {/* header */}
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'11px 16px',background:C.deep,borderBottom:`1px solid ${C.b0}`}}>
          <span style={{fontFamily:'JetBrains Mono,monospace',fontSize:10,color:C.gold,letterSpacing:'.12em',fontWeight:700}}>✎ ADD NOTE TO REPORT</span>
          <button onClick={onClose} style={{background:'none',border:'none',color:C.t3,cursor:'pointer',fontSize:20,lineHeight:1}}>×</button>
        </div>
        {/* tag row */}
        <div style={{display:'flex',gap:5,padding:'12px 16px 8px',flexWrap:'wrap'}}>
          {NOTE_TAGS.map(t=>(
            <button key={t.id} onClick={()=>setTag(t.id)} style={{
              padding:'4px 10px',border:`1px solid ${tag===t.id?t.color:C.b1}`,
              background:tag===t.id?t.color+'20':'transparent',color:tag===t.id?t.color:C.t3,
              fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,borderRadius:2,cursor:'pointer',
            }}>{t.label}</button>
          ))}
        </div>
        {/* preview strip */}
        <div style={{margin:'0 16px 8px',padding:'6px 10px',borderLeft:`3px solid ${tm.color}`,background:C.raised,borderRadius:2}}>
          <span style={{fontSize:8,color:tm.color,fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{tm.label}</span>
        </div>
        {/* textarea */}
        <div style={{padding:'0 16px'}}>
          <textarea value={text} onChange={e=>setText(e.target.value)}
            placeholder="Your finding, hypothesis, observation, or to-do…"
            style={{width:'100%',minHeight:100,background:C.deep,border:`1px solid ${C.b2}`,color:C.t1,padding:10,fontSize:12,fontFamily:'inherit',borderRadius:3,resize:'vertical',outline:'none',boxSizing:'border-box',lineHeight:1.75}}/>
        </div>
        {/* actions */}
        <div style={{display:'flex',justifyContent:'flex-end',gap:8,padding:'12px 16px'}}>
          <button onClick={onClose} style={{padding:'6px 16px',background:'none',border:`1px solid ${C.b1}`,color:C.t3,borderRadius:3,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',fontSize:9}}>CANCEL</button>
          <button onClick={()=>{if(text.trim()){onSave({text:text.trim(),tag,id:`note_${Date.now()}`,ts:new Date().toISOString()});onClose();}}}
            style={{padding:'6px 18px',background:tm.color,border:'none',color:C.void,borderRadius:3,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700}}>
            ADD TO REPORT
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Section drag wrapper ──────────────────────────────────────────────────────
function DragSection({
  title,
  color,
  children,
  onRemove,
  required,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  readOnly,
}) {
  return (
    <div style={{marginBottom:28}}>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
        <h2 style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:16,color,margin:0,flex:1}}>{title}</h2>
        <div data-noprint="true" style={{display:readOnly?'none':'flex',gap:4,alignItems:'center'}}>
          <button
            onClick={onMoveUp}
            disabled={!canMoveUp}
            title="Move section up"
            style={{
              background:'none',
              border:`1px solid ${canMoveUp?C.b1:C.b0}`,
              color:canMoveUp?C.t3:C.t4,
              fontSize:10,
              borderRadius:2,
              cursor:canMoveUp?'pointer':'not-allowed',
              padding:'1px 7px',
              fontFamily:'JetBrains Mono,monospace',
              opacity:canMoveUp?1:.5,
            }}
          >↑</button>
          <button
            onClick={onMoveDown}
            disabled={!canMoveDown}
            title="Move section down"
            style={{
              background:'none',
              border:`1px solid ${canMoveDown?C.b1:C.b0}`,
              color:canMoveDown?C.t3:C.t4,
              fontSize:10,
              borderRadius:2,
              cursor:canMoveDown?'pointer':'not-allowed',
              padding:'1px 7px',
              fontFamily:'JetBrains Mono,monospace',
              opacity:canMoveDown?1:.5,
            }}
          >↓</button>
        </div>
        {!required&&!readOnly&&onRemove&&(
          <button onClick={onRemove} style={{background:'none',border:`1px solid ${C.b1}`,color:C.t4,fontSize:11,borderRadius:2,cursor:'pointer',padding:'1px 7px',fontFamily:'JetBrains Mono,monospace'}}>HIDE</button>
        )}
      </div>
      <div style={{height:1,background:`linear-gradient(90deg,${color}55 0%,transparent 80%)`,marginBottom:14}}/>
      {children}
    </div>
  );
}

// ── Note block (draggable inside report) ──────────────────────────────────────
function NoteBlock({ note, onDelete, onEdit, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) {
  const [editing, setEditing] = useState(false);
  const [draft,   setDraft]   = useState(note.text);
  const tm = tagMeta(note.tag);
  return (
    <div
      style={{
        background:C.surface,border:`1px solid ${tm.color}33`,borderLeft:`3px solid ${tm.color}`,
        borderRadius:3,padding:'10px 13px',marginBottom:10,
        opacity:1,
      }}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
        <div style={{display:'flex',alignItems:'center',gap:7}}>
          <span style={{fontSize:8,color:tm.color,letterSpacing:'.1em',fontFamily:'JetBrains Mono,monospace',fontWeight:700}}>{tm.label}</span>
          <span style={{fontSize:8,color:C.t4}}>{new Date(note.ts).toLocaleString('en',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
        </div>
        <div data-noprint="true" style={{display:'flex',gap:6}}>
          <button
            onClick={onMoveUp}
            disabled={!canMoveUp}
            title="Move note up"
            style={{background:'none',border:'none',color:canMoveUp?C.t3:C.t4,cursor:canMoveUp?'pointer':'not-allowed',fontSize:10,fontFamily:'JetBrains Mono,monospace',opacity:canMoveUp?1:.5}}
          >↑</button>
          <button
            onClick={onMoveDown}
            disabled={!canMoveDown}
            title="Move note down"
            style={{background:'none',border:'none',color:canMoveDown?C.t3:C.t4,cursor:canMoveDown?'pointer':'not-allowed',fontSize:10,fontFamily:'JetBrains Mono,monospace',opacity:canMoveDown?1:.5}}
          >↓</button>
          <button onClick={()=>setEditing(e=>!e)} style={{background:'none',border:'none',color:C.t3,cursor:'pointer',fontSize:10,fontFamily:'JetBrains Mono,monospace'}}>✎</button>
          <button onClick={()=>onDelete(note.id)} style={{background:'none',border:'none',color:C.red,cursor:'pointer',fontSize:14,lineHeight:1}}>×</button>
        </div>
      </div>
      {editing?(
        <div>
          <textarea value={draft} onChange={e=>setDraft(e.target.value)}
            style={{width:'100%',minHeight:60,background:C.deep,border:`1px solid ${C.b2}`,color:C.t1,padding:8,fontSize:12,fontFamily:'inherit',borderRadius:3,resize:'vertical',outline:'none',boxSizing:'border-box'}}/>
          <div style={{display:'flex',gap:7,justifyContent:'flex-end',marginTop:6}}>
            <button onClick={()=>setEditing(false)} style={{padding:'3px 12px',background:'none',border:`1px solid ${C.b1}`,color:C.t3,borderRadius:2,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',fontSize:8}}>CANCEL</button>
            <button onClick={()=>{onEdit(note.id,draft.trim());setEditing(false);}} style={{padding:'3px 12px',background:tm.color,border:'none',color:C.void,borderRadius:2,cursor:'pointer',fontFamily:'JetBrains Mono,monospace',fontSize:8,fontWeight:700}}>SAVE</button>
          </div>
        </div>
      ):(
        <div style={{fontSize:12,color:C.t1,lineHeight:1.75}}>{note.text}</div>
      )}
    </div>
  );
}

// ── Dashboard charts section ──────────────────────────────────────────────────
const LAYOUT_OPTS = [{id:'full',label:'⬛ FULL',w:'100%'},{id:'half',label:'◫ HALF',w:'49%'},{id:'third',label:'⊟ THIRD',w:'32%'}];

function DashboardChartsSection({ dashboardCards, rows, colorMap, profile, onRemoveChart, precomputedData }) {
  const [layouts,    setLayouts]    = useState({});
  const [annotating, setAnnotating] = useState(null);
  const [localAnns,  setLocalAnns]  = useState({});
  if(!dashboardCards?.length) return (
    <div style={{padding:24,textAlign:'center',color:C.t4,background:C.raised,borderRadius:4,border:`2px dashed ${C.b1}`}}>
      <div style={{fontSize:20,marginBottom:6,opacity:0.3}}>★</div>
      <div style={{fontSize:12,color:C.t3,marginBottom:3}}>No report charts pinned yet</div>
      <div style={{fontSize:11}}>Pin charts with ☆ from Distribution, Correlation, Statistics, or Visualization tabs</div>
    </div>
  );
  const getLayout = id => layouts[id]||'half';
  const getAnn    = card => localAnns[card.id]!==undefined?localAnns[card.id]:card.annotation;
  // group into rows
  const rows_grouped=[];let cur=[],curW=0;
  dashboardCards.forEach(card=>{
    const lw={full:100,half:50,third:34}[getLayout(card.id)]||50;
    if(curW+lw>100&&cur.length){rows_grouped.push(cur);cur=[card];curW=lw;}
    else{cur.push(card);curW+=lw;}
  });
  if(cur.length)rows_grouped.push(cur);
  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      {/* layout strip */}
      <div data-noprint="true" style={{display:'flex',gap:5,flexWrap:'wrap',padding:'8px 12px',background:C.raised,border:`1px solid ${C.b1}`,borderRadius:4,alignItems:'center'}}>
        <span style={{fontSize:8,color:C.t4,fontFamily:'JetBrains Mono,monospace',marginRight:4}}>LAYOUT PER CHART:</span>
        {dashboardCards.map(card=>(
          <div key={card.id} style={{display:'flex',alignItems:'center',gap:3,background:C.surface,border:`1px solid ${C.b1}`,borderRadius:3,padding:'2px 7px'}}>
            <span style={{fontSize:8,color:C.t2,maxWidth:70,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{card.chart.title.slice(0,10)}</span>
            {LAYOUT_OPTS.map(opt=>(
              <button key={opt.id} onClick={()=>setLayouts(l=>({...l,[card.id]:opt.id}))}
                style={{padding:'1px 5px',border:`1px solid ${getLayout(card.id)===opt.id?C.cyan:C.b1}`,background:getLayout(card.id)===opt.id?C.cyan+'20':'transparent',color:getLayout(card.id)===opt.id?C.cyan:C.t4,fontSize:7,borderRadius:2,cursor:'pointer',fontFamily:'JetBrains Mono,monospace'}}>{opt.label}</button>
            ))}
          </div>
        ))}
      </div>
      {/* chart collage */}
      {rows_grouped.map((row,ri)=>(
        <div key={ri} style={{display:'flex',gap:12,flexWrap:'wrap'}}>
          {row.map(card=>{
            const lw=LAYOUT_OPTS.find(o=>o.id===getLayout(card.id))?.w||'49%';
            const ann=getAnn(card);
            const elId=`rc-${card.id}`;
            const accent=colorMap?.[card.chart.col||card.chart.xCol]||C.cyan;
            return (
              <div key={card.id} id={elId} style={{width:lw,minWidth:220,flex:lw==='100%'?'1 1 100%':'none',background:C.surface,border:`1px solid ${C.b1}`,borderTop:`2px solid ${accent}`,borderRadius:4,overflow:'hidden'}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'6px 10px',background:C.deep,borderBottom:`1px solid ${C.b0}`}}>
                  <div style={{fontSize:9,color:accent,letterSpacing:'.1em',fontWeight:700,fontFamily:'JetBrains Mono,monospace',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:160}}>{card.chart.title}</div>
                  <div data-noprint="true" style={{display:'flex',gap:4,flexShrink:0}}>
                    <button onClick={()=>setAnnotating(card.id)} style={{width:20,height:20,background:ann?.text?C.gold+'20':'none',border:`1px solid ${ann?.text?C.gold:C.b1}`,color:ann?.text?C.gold:C.t4,borderRadius:2,cursor:'pointer',fontSize:10,display:'inline-flex',alignItems:'center',justifyContent:'center'}}>✎</button>
                    <button onClick={()=>downloadChartPNG(elId,card.chart.title)} style={{width:20,height:20,background:'none',border:`1px solid ${C.b1}`,color:C.t4,borderRadius:2,cursor:'pointer',fontSize:10,display:'inline-flex',alignItems:'center',justifyContent:'center'}}>↓</button>
                    <button
                      onClick={() => onRemoveChart?.(card.id)}
                      title="Remove chart from report"
                      style={{width:20,height:20,background:'none',border:`1px solid ${C.b1}`,color:C.red,borderRadius:2,cursor:'pointer',fontSize:12,display:'inline-flex',alignItems:'center',justifyContent:'center',lineHeight:1}}
                    >×</button>
                  </div>
                </div>
                <div style={{padding:'8px 10px 4px'}}><RenderChart chart={card.chart} rows={rows} colorMap={colorMap} precomputedData={precomputedData} cardData={card._cardData || null}/></div>
                {(() => {
                  const col = card.chart.col || card.chart.xCol;
                  const colProfile = profile?.colProfiles?.find(p => p.col === col);
                  const shape = colProfile?.shape?.skewLabel;
                  return shape ? (
                    <div style={{padding:'4px 10px',background:C.raised,borderTop:`1px solid ${C.b0}`,fontSize:8,color:C.t4,fontFamily:'JetBrains Mono,monospace'}}>
                      Distribution: {shape}
                    </div>
                  ) : null;
                })()}
                {ann?.text&&<AnnotationDisplay annotation={ann} compact/>}
                {annotating===card.id&&(
                  <AnnotationModal initial={ann} onSave={a=>{setLocalAnns(la=>({...la,[card.id]:a.text?a:null}));setAnnotating(null);}} onClose={()=>setAnnotating(null)}/>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ── MAIN REPORT COMPOSER ──────────────────────────────────────────────────────
export default function ReportComposer({ experiment, dashboardCards, profile, precomputed = null, readOnly = false, reportState = null, onStateChange = null }) {
  const { ds } = experiment;
  const initialReportState = useMemo(() => normalizeReportComposerState(reportState, ds), [reportState, ds]);
  const [sections,    setSections]    = useState(() => new Set(initialReportState.sections));
  const [secOrder,    setSecOrder]    = useState(() => initialReportState.secOrder);
  const [notes,       setNotes]       = useState(() => initialReportState.notes);
  const [hiddenChartIds, setHiddenChartIds] = useState(() => new Set(initialReportState.hiddenChartIds));
  const [noteModal,   setNoteModal]   = useState(false);
  const [title,       setTitle]       = useState(() => initialReportState.title);
  const reportRef = useRef();

  // Derived data
  const types   = useMemo(()=>{
    if(precomputed?.col_profiles?.length) return precomputed.col_profiles.map(p=>({col:p.col,type:p.type,uniqueCount:p.uniqueCount}));
    return ds?inferTypes(ds.headers,ds.rows):[];
  },[ds,precomputed]);
  const numCols = useMemo(()=>types.filter(t=>t.type==='numeric').map(t=>t.col),[types]);
  const catCols = useMemo(()=>types.filter(t=>t.type==='categorical').map(t=>t.col),[types]);
  const allStats= useMemo(()=>{
    if(precomputed?.col_profiles?.length) return Object.fromEntries(precomputed.col_profiles.filter(p=>p.stats).map(p=>[p.col,{...p.stats,nulls:precomputed.null_map?.[p.col]??0}]));
    if(!ds)return{};
    return numCols.reduce((acc,col)=>({...acc,[col]:colStats(ds.rows,col)}),{});
  },[ds,numCols.join(),precomputed]);
  const topCorr = useMemo(()=>{
    if(precomputed?.correlations?.length) return precomputed.correlations;
    if(!ds||numCols.length<2) return [];
    const pairs=[];
    for(let i=0;i<Math.min(numCols.length,10);i++)
      for(let j=i+1;j<Math.min(numCols.length,10);j++)
        pairs.push({a:numCols[i],b:numCols[j],r:pearson(ds.rows.slice(0,2000),numCols[i],numCols[j])});
    return pairs.sort((a,b)=>Math.abs(b.r)-Math.abs(a.r)).slice(0,6);
  },[ds,numCols.join(),precomputed]);
  const nullCols= useMemo(()=>{
    if(precomputed?.null_map){const tot=precomputed.dataset?.rows||ds?.rows?.length||1;return Object.entries(precomputed.null_map).filter(([,n])=>n>0).map(([h,n])=>({h,n,pct:+(n/tot*100).toFixed(1)}));}
    return ds?ds.headers.map(h=>({h,n:ds.rows.filter(r=>!r[h]||r[h]==='').length})).filter(c=>c.n>0).map(c=>({...c,pct:+(c.n/ds.rows.length*100).toFixed(1)})):[];
  },[ds,precomputed]);
  const pv = usePaletteVersion();
  const colorMap= useMemo(()=>{const m={};(ds?.headers||[]).forEach((h,i)=>{m[h]=PALETTE[i%PALETTE.length];});return m;},[ds,pv]);
  const precomputedDataMap = useMemo(() => {
    if (!precomputed?.col_profiles?.length) return null;
    return Object.fromEntries(precomputed.col_profiles.map(p => [p.col, p]));
  }, [precomputed]);
  const [corrTab, setCorrTab] = useState('positive');
  const posCorr = topCorr.filter(p => p.r >= 0);
  const negCorr = topCorr.filter(p => p.r < 0);
  const rColor = v => Math.abs(v)>0.7?(v>0?C.green:C.red):Math.abs(v)>0.4?C.cyan:C.t3;

  // Pre-compute & memoize scatter data + options for correlation section (avoids recomputing ALL rows every render)
  const corrScatterOpts = useMemo(() => topCorr.slice(0, 6).map(p => {
    const stored = precomputed?.corr_scatter?.find(cs => cs.a === p.a && cs.b === p.b);
    let pts = stored?.pts;
    if (!pts && ds) {
      const step = ds.rows.length > 1500 ? Math.ceil(ds.rows.length / 1500) : 1;
      pts = ds.rows.filter((_, i) => i % step === 0).slice(0, 1500)
        .map(r => [parseFloat(r[p.a]), parseFloat(r[p.b])]).filter(pt => !isNaN(pt[0]) && !isNaN(pt[1]));
    }
    return {
      backgroundColor: 'transparent',
      tooltip: { trigger: 'item', backgroundColor: C.void, borderColor: C.b2, textStyle: { color: C.t2, fontSize: 9 } },
      grid: { top: 4, right: 4, bottom: 16, left: 4, containLabel: true },
      xAxis: { type: 'value', scale: true, axisLabel: { color: C.t4, fontSize: 8 }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { show: false } },
      yAxis: { type: 'value', scale: true, axisLabel: { color: C.t4, fontSize: 8 }, axisLine: { show: false }, axisTick: { show: false }, splitLine: { lineStyle: { color: C.b0, type: 'dashed' } } },
      series: [{ type: 'scatter', data: pts || [], symbolSize: 4, itemStyle: { color: rColor(p.r), opacity: 0.6 } }],
    };
  }), [topCorr, ds, precomputed, pv]);

  const distributionCards = useMemo(() => {
    const cards = [];
    const seen = new Set();
    const totalRows = precomputed?.dataset?.rows || ds?.rows?.length || 0;
    const addCard = (card, fallbackSection = 'distributions') => {
      if (!card?.chart || hiddenChartIds.has(card.id)) return;
      const reportSection = card.chart.reportSection || inferPinnedReportSection(card) || fallbackSection;
      const normalized = {
        ...card,
        chart: {
          ...card.chart,
          reportSection,
        },
      };
      const key = reportChartKey(normalized.chart);
      if (seen.has(key)) return;
      seen.add(key);
      cards.push(normalized);
    };

    (dashboardCards || []).forEach(card => {
      if (!card?.chart) return;
      const inferredSection = inferPinnedReportSection(card);
      if (inferredSection === 'visualization') return;
      if (inferredSection === 'distributions' || isDistributionChartType(card.chart.type)) {
        addCard(card, 'distributions');
      }
    });

    if (numCols.length >= 3) {
      addCard({
        id: 'auto_dist_num_overview',
        chart: {
          id: 'auto_dist_num_overview_chart',
          type: 'num_overview',
          cols: numCols.slice(0, 8),
          title: 'Numeric Columns Overview',
          subtitle: 'mean & max',
        },
        annotation: null,
      });
    }

    numCols.forEach((col, idx) => {
      addCard({
        id: `auto_dist_hist_${col}`,
        chart: {
          id: `auto_dist_hist_chart_${idx}`,
          type: 'hist',
          col,
          title: `${col} — Distribution`,
          subtitle: 'Histogram',
        },
        annotation: null,
      });
    });

    const catCandidates = types.filter(t => {
      if (t.type !== 'categorical' || t.uniqueCount < 2) return false;
      return !(totalRows > 30 && t.uniqueCount / Math.max(totalRows, 1) > 0.9);
    });
    catCandidates.forEach((c, idx) => {
      const chartType = c.uniqueCount <= 10 ? 'donut' : 'hbar';
      addCard({
        id: `auto_dist_cat_${c.col}`,
        chart: {
          id: `auto_dist_cat_chart_${idx}`,
          type: chartType,
          col: c.col,
          title: `${c.col} — ${chartType === 'donut' ? 'Composition' : 'Top Categories'}`,
          subtitle: `${c.uniqueCount} categories`,
        },
        annotation: null,
      });
    });

    return cards;
  }, [dashboardCards, ds, numCols, types, hiddenChartIds, precomputed]);

  const health = useMemo(() => {
    if (precomputed) {
      const totalRows = precomputed.dataset?.rows || ds?.rows?.length || 0;
      const totalNulls = nullCols.reduce((s, c) => s + c.n, 0);
      const totalCells = totalRows * (ds?.headers?.length || 0);
      const nullPct = totalCells ? +((totalNulls / totalCells) * 100).toFixed(2) : 0;
      const dupCount = precomputed.metrics?.duplicates || 0;
      const cp = precomputed.col_profiles || [];
      const skewed = cp.filter(p => Math.abs(p?.stats?.skew || 0) > 1).map(p => p.col);
      const constants = cp.filter(p => p.flags?.some(f => f.label === 'CONSTANT')).map(p => p.col);
      const highCard = cp.filter(p => p.flags?.some(f => f.label === 'HIGH CARD')).map(p => p.col);
      const idLike = cp.filter(p => p.flags?.some(f => f.label === 'LIKELY ID')).map(p => p.col);
      const qualityScore = typeof precomputed.quality_score === 'number' ? precomputed.quality_score
        : Math.max(0, Math.round(100 - nullPct * 1.2 - (dupCount / Math.max(totalRows, 1)) * 25 - constants.length * 5));
      const completeRowPct = +(100 - nullPct).toFixed(1);
      return { totalCells, totalNulls, nullPct, dupCount,
        completeRows: Math.round(totalRows * completeRowPct / 100), completeRowPct,
        skewed, outlierHeavy: [], constants, highCard, idLike, qualityScore };
    }
    if (!ds) return null;
    const totalCells = ds.rows.length * ds.headers.length;
    const totalNulls = nullCols.reduce((sum, col) => sum + col.n, 0);
    const nullPct = totalCells ? +((totalNulls / totalCells) * 100).toFixed(2) : 0;
    const rowKeys = ds.rows.map(r => ds.headers.map(h => r[h]).join('\x00'));
    const dupCount = rowKeys.length - new Set(rowKeys).size;
    const completeRows = ds.rows.filter(r => ds.headers.every(h => r[h] !== '' && r[h] != null)).length;
    const completeRowPct = ds.rows.length ? +((completeRows / ds.rows.length) * 100).toFixed(1) : 0;
    const colProfiles = profile?.colProfiles || [];
    const skewed = colProfiles.filter(p => Math.abs(p?.stats?.skew || 0) > 1).map(p => p.col);
    const outlierHeavy = colProfiles.filter(p => (p?.shape?.outlierPct || 0) > 5).map(p => ({ col: p.col, pct: p.shape.outlierPct }));
    const constants = colProfiles.filter(p => p.flags?.some(f => f.label === 'CONSTANT')).map(p => p.col);
    const highCard = colProfiles.filter(p => p.flags?.some(f => f.label === 'HIGH CARD')).map(p => p.col);
    const idLike = colProfiles.filter(p => p.flags?.some(f => f.label === 'LIKELY ID')).map(p => p.col);
    const qualityScore = typeof profile?.qualityScore === 'number'
      ? profile.qualityScore
      : Math.max(0, Math.round(100 - nullPct * 1.2 - (dupCount / Math.max(ds.rows.length, 1)) * 25 - constants.length * 5));
    return {
      totalCells, totalNulls, nullPct, dupCount, completeRows, completeRowPct,
      skewed, outlierHeavy, constants, highCard, idLike, qualityScore,
    };
  }, [ds, nullCols, profile, precomputed]);

  const qualityNotes = useMemo(() => {
    if (precomputed?.quality_insights?.length) return precomputed.quality_insights.map(text => {
      const t = text.toLowerCase();
      const kind = (t.startsWith('no ') || t.includes('no missing') || t.includes('no duplicate') || t.includes('no null')) ? 'good'
        : (t.includes('high') || t.includes('risk') || t.includes('consider')) ? 'risk' : 'warn';
      return { kind, text };
    });
    if (!health) return [];
    const notes = [];
    if (health.nullPct === 0) notes.push({ kind: 'good', text: 'No missing values detected across the dataset.' });
    else if (health.nullPct > 20) notes.push({ kind: 'risk', text: `Missingness is high (${health.nullPct}%). Consider imputation or null-row filtering before modeling.` });
    else notes.push({ kind: 'warn', text: `Missingness is moderate (${health.nullPct}%). Validate null-heavy columns before downstream use.` });

    if (health.dupCount > 0) notes.push({ kind: 'warn', text: `${health.dupCount.toLocaleString()} duplicate rows detected; deduplicate to reduce bias.` });
    else notes.push({ kind: 'good', text: 'No duplicate rows found.' });

    if (health.constants.length) notes.push({ kind: 'risk', text: `Constant columns: ${health.constants.join(', ')}. These add no signal.` });
    if (health.skewed.length) notes.push({ kind: 'warn', text: `High skewness in ${health.skewed.length} column(s): ${health.skewed.slice(0, 6).join(', ')}${health.skewed.length > 6 ? '…' : ''}.` });
    if (health.highCard.length) notes.push({ kind: 'warn', text: `High-cardinality categoricals: ${health.highCard.join(', ')}.` });
    if (topCorr.length) notes.push({ kind: 'good', text: `Top relationship: ${topCorr[0].a} ↔ ${topCorr[0].b} (r=${topCorr[0].r.toFixed(3)}).` });
    return notes;
  }, [health, topCorr, precomputed]);

  const reportVizCards = useMemo(() => {
    if (!ds) return [];
    const NON_SCATTER = t => t !== 'scatter' && t !== 'bubble';
    const cards = [];
    const seen = new Set();
    const addCard = (card, fallbackSection = 'visualization') => {
      if (!card?.chart || hiddenChartIds.has(card.id)) return;
      const reportSection = card.chart.reportSection || inferPinnedReportSection(card) || fallbackSection;
      const normalized = {
        ...card,
        chart: {
          ...card.chart,
          reportSection,
        },
      };
      const key = reportChartKey(normalized.chart);
      if (seen.has(key)) return;
      seen.add(key);
      cards.push(normalized);
    };

    // 1. User-pinned charts from visualization, plus legacy non-distribution pins
    (dashboardCards || []).forEach((card, idx) => {
      if (!card?.chart) return;
      const inferredSection = inferPinnedReportSection(card);
      const keepBecauseVisualization = inferredSection === 'visualization';
      if (!keepBecauseVisualization && isDistributionChartType(card.chart.type)) return;
      if (!NON_SCATTER(card.chart.type)) return;
      addCard({ ...card, id: card.id || `dash_${idx}` }, 'visualization');
    });

    // 2. If precomputed viz_cards are available (View Report), use them directly
    if (precomputed?.viz_cards?.length) {
      precomputed.viz_cards.forEach((vc, idx) => {
        if (!vc?.chart) return;
        addCard({ id: `stored_viz_${idx}`, chart: vc.chart, annotation: null, _cardData: vc.data || null }, 'visualization');
      });
      return cards;
    }

    // 3. Auto-generated charts (broad variety, no scatter/bubble/distribution)
    const autoCharts = buildAutoCharts(ds.rows, ds.headers, types);
    autoCharts.forEach((chart, idx) => {
      if (!NON_SCATTER(chart.type)) return;
      if (isDistributionChartType(chart.type)) return;
      addCard({ id: `auto_viz_${idx}`, chart, annotation: null }, 'visualization');
    });

    return cards;
  }, [dashboardCards, ds, types, hiddenChartIds, precomputed]);

  const has = id => sections.has(id);
  const visibleSectionOrder = useMemo(() => secOrder.filter(id => sections.has(id)), [secOrder, sections]);
  const toggleSec = (id,req) => { if(req)return; setSections(prev=>{const n=new Set(prev);n.has(id)?n.delete(id):n.add(id);return n;}); };

  // Notes CRUD
  const addNote = useCallback((note) => {
    const fallbackAnchor = sections.has('conclusion') ? 'conclusion' : (visibleSectionOrder.at(-1) || visibleSectionOrder[0] || null);
    setNotes(prev => [...prev, { ...note, anchorSection: fallbackAnchor }]);
  }, [sections, visibleSectionOrder]);
  const deleteNote = id    => setNotes(p=>p.filter(n=>n.id!==id));
  const editNote   = (id,text) => setNotes(p=>p.map(n=>n.id===id?{...n,text}:n));

  useEffect(() => {
    if (!visibleSectionOrder.length) return;
    setNotes(prev => {
      let changed = false;
      const fallback = visibleSectionOrder.at(-1);
      const next = prev.map(note => {
        if (note.anchorSection && visibleSectionOrder.includes(note.anchorSection)) return note;
        changed = true;
        return { ...note, anchorSection: fallback };
      });
      return changed ? next : prev;
    });
  }, [visibleSectionOrder]);

  useEffect(() => {
    if (readOnly || typeof onStateChange !== 'function') return;
    onStateChange({
      title,
      sections: [...sections],
      secOrder,
      notes,
      hiddenChartIds: [...hiddenChartIds],
    });
  }, [title, sections, secOrder, notes, hiddenChartIds, readOnly, onStateChange]);

  const moveSection = useCallback((id, dir) => {
    setSecOrder(prev => {
      const idx = prev.indexOf(id);
      if (idx < 0) return prev;
      const next = idx + dir;
      if (next < 0 || next >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[next]] = [arr[next], arr[idx]];
      return arr;
    });
  }, []);

  const removeChartFromReport = useCallback((chartId) => {
    if (!chartId) return;
    setHiddenChartIds(prev => new Set(prev).add(chartId));
  }, []);

  const moveNote = useCallback((noteId, dir) => {
    setNotes(prev => {
      const idx = prev.findIndex(n => n.id === noteId);
      if (idx < 0) return prev;
      const current = prev[idx];
      const currentAnchor = current.anchorSection;
      const sameAnchorIdx = prev.map((n, i) => ({ n, i })).filter(x => x.n.anchorSection === currentAnchor).map(x => x.i);
      const posInAnchor = sameAnchorIdx.indexOf(idx);

      if (dir < 0) {
        if (posInAnchor > 0) {
          const swapIdx = sameAnchorIdx[posInAnchor - 1];
          const arr = [...prev];
          [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
          return arr;
        }
        const secPos = visibleSectionOrder.indexOf(currentAnchor);
        if (secPos <= 0) return prev;
        const arr = [...prev];
        arr[idx] = { ...arr[idx], anchorSection: visibleSectionOrder[secPos - 1] };
        return arr;
      }

      if (dir > 0) {
        if (posInAnchor < sameAnchorIdx.length - 1) {
          const swapIdx = sameAnchorIdx[posInAnchor + 1];
          const arr = [...prev];
          [arr[idx], arr[swapIdx]] = [arr[swapIdx], arr[idx]];
          return arr;
        }
        const secPos = visibleSectionOrder.indexOf(currentAnchor);
        if (secPos < 0 || secPos >= visibleSectionOrder.length - 1) return prev;
        const arr = [...prev];
        arr[idx] = { ...arr[idx], anchorSection: visibleSectionOrder[secPos + 1] };
        return arr;
      }

      return prev;
    });
  }, [visibleSectionOrder]);

  const renderAnchoredNotes = useCallback((sid) => {
    const anchored = notes.filter(n => n.anchorSection === sid);
    if (!anchored.length) return null;
    const secPos = visibleSectionOrder.indexOf(sid);
    return (
      <div style={{ marginTop: -8, marginBottom: 20 }}>
        <div style={{fontSize:9,color:C.gold,letterSpacing:'.1em',fontFamily:'JetBrains Mono,monospace',fontWeight:700,marginBottom:8}}>
          NOTES ({anchored.length})
        </div>
        {anchored.map((note, noteIdx) => (
          <NoteBlock
            key={note.id}
            note={note}
            onDelete={deleteNote}
            onEdit={editNote}
            onMoveUp={() => moveNote(note.id, -1)}
            onMoveDown={() => moveNote(note.id, 1)}
            canMoveUp={noteIdx > 0 || secPos > 0}
            canMoveDown={noteIdx < anchored.length - 1 || secPos < visibleSectionOrder.length - 1}
          />
        ))}
      </div>
    );
  }, [notes, visibleSectionOrder, moveNote, deleteNote, editNote]);

  // Export HTML — ECharts-powered, self-contained
  const exportHTML = () => {
    const el = reportRef.current;
    if (!el) return;

    const PAL    = ['#22d3ee','#4ade80','#a78bfa','#fbbf24','#fb923c','#f87171','#f472b6','#60a5fa','#34d399','#67e8f9'];
    const GRID_C = '#0f1a2e';
    const TICK_C = '#627795';
    const TEXT_C = '#b6c3d8';
    const BG     = '#020508';
    const rows   = ds.rows;

    // shared ECharts option helpers (hardcoded hex — no C.* refs for JSON safety)
    const xCat = (data, rotate=0) => ({type:'category',data,axisLabel:{color:TICK_C,fontSize:8,rotate,interval:rotate?0:'auto'},axisLine:{lineStyle:{color:'#1e293b'}},axisTick:{show:false}});
    const xVal = () => ({type:'value',scale:true,axisLabel:{color:TICK_C,fontSize:8},axisLine:{lineStyle:{color:'#1e293b'}},axisTick:{show:false},splitLine:{show:false}});
    const yVal = () => ({type:'value',axisLabel:{color:TICK_C,fontSize:8},axisLine:{show:false},axisTick:{show:false},splitLine:{lineStyle:{color:GRID_C,type:'dashed'}}});
    const tip  = (trigger='axis') => ({trigger,backgroundColor:BG,borderColor:'#2d3f55',textStyle:{color:TEXT_C,fontSize:10,fontFamily:'JetBrains Mono,monospace'}});
    const grid = (b=32) => ({top:8,right:8,bottom:b,left:8,containLabel:true});
    const base = () => ({backgroundColor:'transparent',tooltip:tip(),grid:grid()});

    // ── Build ECharts option per card ─────────────────────────────────────────
    const buildEOpt = (card) => {
      const { chart } = card;
      const accent = colorMap?.[chart.col || chart.xCol] || '#22d3ee';
      try {
        switch (chart.type) {
          case 'hist': {
            const data = getHistogram(rows, chart.col, 20);
            const s = colStats(rows, chart.col);
            const mean = s ? s.mean : null;
            return { ...base(), grid:grid(28),
              xAxis:{type:'value',min:'dataMin',max:'dataMax',axisLabel:{color:TICK_C,fontSize:8},axisLine:{lineStyle:{color:'#1e293b'}},axisTick:{show:false},splitLine:{show:false}},
              yAxis:yVal(), tooltip:tip(),
              series:[{ type:'bar',barWidth:'95%',data:data.map(d=>[d.label,d.count]),itemStyle:{color:accent,borderRadius:[2,2,0,0]},
                ...(mean!=null?{markLine:{silent:true,symbol:'none',lineStyle:{color:'#fbbf24',type:'dashed',width:1.5},data:[{xAxis:mean,name:'mean'}]}}:{}) }]
            };
          }
          case 'donut': case 'pie': {
            const data = getValueCounts(rows, chart.col, 12);
            return { backgroundColor:'transparent', tooltip:tip('item'),
              legend:{orient:'vertical',right:4,top:'center',textStyle:{color:TICK_C,fontSize:9}},
              series:[{ type:'pie',radius:chart.type==='donut'?['45%','72%']:['0%','72%'],padAngle:2,
                data:data.map((d,i)=>({name:String(d.name).slice(0,20),value:d.count,itemStyle:{color:PAL[i%PAL.length]}})),
                label:{show:false} }]
            };
          }
          case 'hbar': {
            const data = getValueCounts(rows, chart.col, 20);
            return { ...base(), grid:{top:0,right:40,bottom:0,left:8,containLabel:true},
              xAxis:xVal(),
              yAxis:{type:'category',data:data.map(d=>String(d.name).slice(0,22)),inverse:true,axisLabel:{color:TEXT_C,fontSize:9},axisLine:{show:false},axisTick:{show:false}},
              series:[{type:'bar',data:data.map((d,i)=>({value:d.count,itemStyle:{color:PAL[i%PAL.length]}})),barMaxWidth:20,itemStyle:{borderRadius:[0,3,3,0]}}]
            };
          }
          case 'radial': {
            const data = getValueCounts(rows, chart.col, 12);
            const maxV = Math.max(...data.map(d=>d.count), 1);
            return { backgroundColor:'transparent', tooltip:tip('item'),
              radar:{indicator:data.map(d=>({name:String(d.name).slice(0,12),max:maxV})),splitLine:{lineStyle:{color:GRID_C}},axisName:{color:TICK_C,fontSize:8},splitArea:{show:false}},
              series:[{type:'radar',data:[{value:data.map(d=>d.count),name:chart.col,areaStyle:{color:accent+'28'},lineStyle:{color:accent,width:2},itemStyle:{color:accent}}]}]
            };
          }
          case 'scatter': {
            const step = rows.length > 1500 ? Math.ceil(rows.length / 1500) : 1;
            const pts = rows.filter((_,i)=>i%step===0).slice(0,1500).map(r=>[parseFloat(r[chart.xCol]),parseFloat(r[chart.yCol])]).filter(p=>!isNaN(p[0])&&!isNaN(p[1]));
            return { ...base(), grid:grid(28),
              xAxis:{...xVal(),name:chart.xCol,nameTextStyle:{color:TICK_C,fontSize:9}},
              yAxis:{...yVal(),name:chart.yCol,nameTextStyle:{color:TICK_C,fontSize:9}},
              series:[{type:'scatter',data:pts,symbolSize:4,itemStyle:{color:accent,opacity:0.6}}]
            };
          }
          case 'bubble': {
            const step = rows.length > 600 ? Math.ceil(rows.length / 600) : 1;
            const samp = rows.filter((_,i)=>i%step===0).slice(0,600);
            const sVals = samp.map(r=>parseFloat(r[chart.sizeCol])).filter(v=>!isNaN(v));
            const mn = Math.min(...sVals)||0, mx = Math.max(...sVals)||1;
            const pts = samp.map(r=>{ const x=parseFloat(r[chart.xCol]),y=parseFloat(r[chart.yCol]),sz=parseFloat(r[chart.sizeCol]); if(isNaN(x)||isNaN(y)) return null; return [x,y,Math.max(3,((isNaN(sz)?0.5:(sz-mn)/((mx-mn)||1)))*26+4)]; }).filter(Boolean);
            return { ...base(), xAxis:xVal(), yAxis:yVal(),
              series:[{type:'scatter',data:pts,_bubbleSize:true,symbolSize:12,itemStyle:{color:accent,opacity:0.6}}]
            };
          }
          case 'groupby': {
            const data = computeAggrData(rows, chart.catCol, chart.numCol, 'mean');
            return { ...base(), grid:grid(40),
              xAxis:xCat(data.map(d=>d.name),-22), yAxis:yVal(),
              series:[{type:'bar',data:data.map((d,i)=>({value:d.value,itemStyle:{color:PAL[i%PAL.length]}})),barMaxWidth:32,itemStyle:{borderRadius:[3,3,0,0]}}]
            };
          }
          case 'num_overview': {
            const cols2 = chart.cols || numCols.slice(0,8);
            const data = cols2.map(col=>{ const s=colStats(rows,col); return s?{name:col.slice(0,12),mean:s.mean,max:s.max}:null; }).filter(Boolean);
            return { ...base(), grid:grid(38), xAxis:xCat(data.map(d=>d.name),-22), yAxis:yVal(),
              series:[
                {type:'bar',data:data.map(d=>d.mean),name:'mean',itemStyle:{color:'#22d3ee',opacity:0.8,borderRadius:[3,3,0,0]},barMaxWidth:28},
                {type:'bar',data:data.map(d=>d.max),name:'max',itemStyle:{color:'#a78bfa',opacity:0.45,borderRadius:[3,3,0,0]},barMaxWidth:28}
              ]
            };
          }
          case 'timeseries': {
            const dateCol=chart.dateCol, valCol=chart.valueCol||chart.yCol;
            if(!dateCol||!valCol) return null;
            const step2=rows.length>150?Math.ceil(rows.length/150):1;
            const pts2=rows.filter((_,i)=>i%step2===0).map(r=>({d:r[dateCol],v:parseFloat(r[valCol])})).filter(p=>p.d&&!isNaN(p.v));
            if(pts2.length<2) return null;
            return { ...base(), grid:grid(32),
              xAxis:{type:'category',data:pts2.map(p=>String(p.d).slice(0,10)),axisLabel:{color:TICK_C,fontSize:8,rotate:22,interval:Math.max(0,Math.floor(pts2.length/6)-1)},axisLine:{lineStyle:{color:'#1e293b'}},axisTick:{show:false}},
              yAxis:yVal(),
              series:[{type:'line',data:pts2.map(p=>p.v),smooth:true,symbol:'none',lineStyle:{color:accent,width:2},areaStyle:{color:accent+'22'},itemStyle:{color:accent}}]
            };
          }
          case 'boxplot': {
            const bVals=rows.map(r=>parseFloat(r[chart.col])).filter(v=>!isNaN(v)).sort((a,b)=>a-b);
            if(bVals.length<5) return null;
            const bn=bVals.length,bq1=bVals[Math.floor(bn*0.25)],bmed=bVals[Math.floor(bn*0.5)],bq3=bVals[Math.floor(bn*0.75)];
            const biqr=bq3-bq1,bwLo=bVals.find(v=>v>=bq1-1.5*biqr)??bVals[0],bwHi=[...bVals].reverse().find(v=>v<=bq3+1.5*biqr)??bVals[bn-1];
            return { backgroundColor:'transparent',
              tooltip:{trigger:'item',backgroundColor:BG,borderColor:'#2d3f55',textStyle:{color:TEXT_C,fontSize:10}},
              grid:{left:52,right:14,top:12,bottom:28},
              xAxis:{type:'category',data:[chart.col],axisLabel:{color:TICK_C,fontSize:9},axisLine:{lineStyle:{color:'#1e293b'}},axisTick:{show:false}},
              yAxis:{type:'value',scale:true,axisLabel:{color:TICK_C,fontSize:8},axisLine:{lineStyle:{color:'#1e293b'}},splitLine:{lineStyle:{color:GRID_C,type:'dashed'}}},
              series:[{type:'boxplot',data:[[bwLo,bq1,bmed,bq3,bwHi]],boxWidth:[30,80],itemStyle:{color:accent+'33',borderColor:accent,borderWidth:1.5}}]
            };
          }
          case 'multibox': {
            const mbCols=(chart.cols||numCols.slice(0,8));
            const mbRes=mbCols.map(col=>{
              const v=rows.map(r=>parseFloat(r[col])).filter(v=>!isNaN(v)).sort((a,b)=>a-b);
              if(v.length<5) return null;
              const n=v.length,q1=v[Math.floor(n*0.25)],med=v[Math.floor(n*0.5)],q3=v[Math.floor(n*0.75)],iqr=q3-q1;
              return {col,box:[v.find(x=>x>=q1-1.5*iqr)??v[0],q1,med,q3,[...v].reverse().find(x=>x<=q3+1.5*iqr)??v[n-1]]};
            }).filter(Boolean);
            if(!mbRes.length) return null;
            const mbR=mbRes.length>4?22:0;
            return { backgroundColor:'transparent',
              tooltip:{trigger:'item',backgroundColor:BG,borderColor:'#2d3f55',textStyle:{color:TEXT_C,fontSize:10}},
              grid:{left:52,right:14,top:12,bottom:mbR?50:30},
              xAxis:{type:'category',data:mbRes.map(r=>r.col),axisLabel:{color:TICK_C,fontSize:8,rotate:mbR,interval:0},axisLine:{lineStyle:{color:'#1e293b'}},axisTick:{show:false}},
              yAxis:{type:'value',scale:true,axisLabel:{color:TICK_C,fontSize:8},axisLine:{lineStyle:{color:'#1e293b'}},splitLine:{lineStyle:{color:GRID_C,type:'dashed'}}},
              series:[{type:'boxplot',data:mbRes.map(r=>r.box),boxWidth:[20,60],itemStyle:{color:'#22d3ee33',borderColor:'#22d3ee',borderWidth:1.5}}]
            };
          }
          case 'treemap': {
            const tmData=getValueCounts(rows,chart.col,30);
            if(!tmData.length) return null;
            return { backgroundColor:'transparent',
              tooltip:{trigger:'item',formatter:p=>`${p.name}: ${p.value}`,backgroundColor:BG,borderColor:'#2d3f55',textStyle:{color:TEXT_C,fontSize:10}},
              series:[{type:'treemap',roam:false,breadcrumb:{show:false},width:'100%',height:'90%',left:0,top:4,
                label:{show:true,fontSize:9,color:'#020508',overflow:'truncate'},
                itemStyle:{borderWidth:1,borderColor:'#060b14',gapWidth:2},
                data:tmData.map((d,i)=>({name:d.name,value:d.count,itemStyle:{color:PAL[i%PAL.length]}}))
              }]
            };
          }
          case 'funnel': {
            const fnData=getValueCounts(rows,chart.col,8).sort((a,b)=>b.count-a.count);
            if(!fnData.length) return null;
            return { backgroundColor:'transparent',
              tooltip:{trigger:'item',formatter:p=>`${p.name}: ${p.value}`,backgroundColor:BG,borderColor:'#2d3f55',textStyle:{color:TEXT_C,fontSize:10}},
              series:[{type:'funnel',sort:'descending',gap:1,left:'8%',width:'84%',top:8,bottom:8,
                label:{show:true,position:'inside',fontSize:9,color:'#020508'},
                itemStyle:{borderWidth:0},
                data:fnData.map((d,i)=>({name:d.name,value:d.count,itemStyle:{color:PAL[i%PAL.length]}}))
              }]
            };
          }
          case 'parallel': {
            const plCols=(chart.cols||numCols.slice(0,8)).filter(c=>rows.some(r=>!isNaN(parseFloat(r[c])))).slice(0,8);
            if(plCols.length<3) return null;
            const plStep=rows.length>400?Math.ceil(rows.length/400):1;
            const plSamp=rows.filter((_,i)=>i%plStep===0).slice(0,400);
            const plExt=plCols.map(col=>{
              const v=plSamp.map(r=>parseFloat(r[col])).filter(v=>!isNaN(v));
              if(!v.length) return [0,1];
              const mn=Math.min(...v),mx=Math.max(...v),pad=(mx-mn)*0.05||1;
              return [mn-pad,mx+pad];
            });
            return { backgroundColor:'transparent',
              parallelAxis:plCols.map((col,i)=>({dim:i,name:col.length>10?col.slice(0,9)+'…':col,min:plExt[i][0],max:plExt[i][1],nameTextStyle:{color:TICK_C,fontSize:8},axisLabel:{color:TICK_C,fontSize:7},axisLine:{lineStyle:{color:'#2d3f55'}},axisTick:{lineStyle:{color:'#2d3f55'}}})),
              parallel:{left:20,right:20,top:30,bottom:10},
              tooltip:{show:false},
              series:[{type:'parallel',data:plSamp.map(r=>plCols.map(c=>{const v=parseFloat(r[c]);return isNaN(v)?null:v;})).filter(d=>d.every(v=>v!==null)),lineStyle:{width:0.7,opacity:0.28,color:'#22d3ee'},smooth:false}]
            };
          }
          default: {
            const xc=chart.xCol||chart.col, yc=chart.yCol;
            if(!xc||!yc) return null;
            const data = computeAggrData(rows, xc, yc, chart.agg||'mean');
            if(!data.length) return null;
            if(chart.type==='line'||chart.type==='area') {
              return { ...base(), grid:grid(), xAxis:xCat(data.map(d=>d.name)), yAxis:yVal(),
                series:[{type:'line',data:data.map(d=>d.value),smooth:true,
                  ...(chart.type==='area'?{areaStyle:{color:accent+'44'}}:{}),
                  lineStyle:{color:accent,width:2},itemStyle:{color:accent},symbol:'none'}]
              };
            }
            return { ...base(), grid:grid(40), xAxis:xCat(data.map(d=>d.name),-22), yAxis:yVal(),
              series:[{type:'bar',data:data.map((d,i)=>({value:d.value,itemStyle:{color:PAL[i%PAL.length]}})),barMaxWidth:32,itemStyle:{borderRadius:[3,3,0,0]}}]
            };
          }
        }
      } catch(e) { return null; }
    };

    // Build ECharts option map for all visible cards
    const chartEOpts = {};
    const allCards = [...distributionCards, ...reportVizCards];
    allCards.forEach(card => { const opt = buildEOpt(card); if(opt) chartEOpts[card.id] = opt; });

    // Correlation inline scatter options
    topCorr.slice(0,6).forEach((p,i) => {
      const rColor = Math.abs(p.r)>0.7?(p.r>0?'#4ade80':'#f87171'):Math.abs(p.r)>0.4?'#22d3ee':'#8396b3';
      const pts = rows.map(r=>[parseFloat(r[p.a]),parseFloat(r[p.b])]).filter(pt=>!isNaN(pt[0])&&!isNaN(pt[1]));
      chartEOpts[`corr_scatter_${i}`] = { backgroundColor:'transparent',
        tooltip:{trigger:'item',backgroundColor:BG,borderColor:'#2d3f55',textStyle:{color:TEXT_C,fontSize:9}},
        grid:{top:4,right:4,bottom:16,left:4,containLabel:true},
        xAxis:{type:'value',scale:true,axisLabel:{color:TICK_C,fontSize:8},axisLine:{show:false},axisTick:{show:false},splitLine:{show:false}},
        yAxis:{type:'value',scale:true,axisLabel:{color:TICK_C,fontSize:8},axisLine:{show:false},axisTick:{show:false},splitLine:{lineStyle:{color:GRID_C,type:'dashed'}}},
        series:[{type:'scatter',data:pts,symbolSize:4,itemStyle:{color:rColor,opacity:0.6}}]
      };
    });

    // ── Clone DOM, strip UI-only elements ───────────────────────────────────
    const clone = el.cloneNode(true);
    clone.querySelectorAll('[data-noprint]').forEach(e => e.remove());

    // ── Replace each ECharts container with a div[data-eda-chart] placeholder
    const H_BY_TYPE = { donut:200, pie:200, hbar:220, hist:170, radial:190, boxplot:190, multibox:200, treemap:210, funnel:210, parallel:240, timeseries:190, groupby:200, num_overview:195 };
    allCards.forEach(card => {
      if(!chartEOpts[card.id]) return;
      const container = clone.querySelector(`#rc-${card.id}`);
      if(!container) return;
      const canvas = container.querySelector('canvas');
      const chartDiv = canvas?.parentElement;
      if(!chartDiv || chartDiv === container) return;
      const h = H_BY_TYPE[card.chart?.type] || 185;
      const placeholder = document.createElement('div');
      placeholder.setAttribute('data-eda-chart', card.id);
      placeholder.setAttribute('data-eda-h', String(h));
      placeholder.style.cssText = `width:100%;height:${h}px;`;
      chartDiv.replaceWith(placeholder);
    });

    // ── Replace correlation scatter chart divs ───────────────────────────────
    topCorr.slice(0,6).forEach((_,i) => {
      const corrDiv = clone.querySelector(`#corr-chart-${i}`);
      if(!corrDiv) return;
      const placeholder = document.createElement('div');
      placeholder.setAttribute('data-eda-chart', `corr_scatter_${i}`);
      placeholder.setAttribute('data-eda-h', '120');
      placeholder.style.cssText = 'width:100%;height:120px;';
      corrDiv.innerHTML = '';
      corrDiv.appendChild(placeholder);
    });

    // Fix any remaining decorative SVGs
    clone.querySelectorAll('svg').forEach(svg => {
      if(!svg.getAttribute('xmlns')) svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    });

    // ── Serialize — escape template-literal special chars in user content ────
    const cfgJSON = JSON.stringify(chartEOpts)
      .replace(/<\/script>/gi, '<\\/script>')
      .replace(/<!--/g, '<\\!--');
    const safeTitle = String(title)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
      .replace(/`/g,'&#96;').replace(/\$\{/g,'&#36;{');
    const body = clone.innerHTML
      .replace(/`/g,'&#96;')
      .replace(/\$\{/g,'&#36;{');

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>${safeTitle}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;700&family=Syne:wght@400;700;800;900&display=swap" rel="stylesheet">
  <script src="https://cdn.jsdelivr.net/npm/echarts@5.5.1/dist/echarts.min.js"><\/script>
  <style>
    *, *::before, *::after { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; }
    body { background: #020508; color: #e2e8f0; font-family: system-ui, -apple-system, 'Segoe UI', sans-serif; line-height: 1.8; min-height: 100vh; }
    .eda-report-wrap { max-width: 1080px; margin: 0 auto; padding: 48px 36px 80px; }
    h1, h2, h3 { font-family: 'Syne', system-ui, sans-serif; }
    ::-webkit-scrollbar { width: 6px; height: 6px; }
    ::-webkit-scrollbar-track { background: transparent; }
    ::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 3px; }
    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
    button { display: none !important; }
    input, textarea { pointer-events: none; }
    [data-eda-chart] { border-radius: 3px; display: block; }
    [data-corr-tab] { display: block !important; }
    select { display: none !important; }
    table { border-collapse: collapse; width: 100%; }
    td, th { word-break: break-word; }
    h2[data-collapsible] { cursor: pointer; user-select: none; }
    h2[data-collapsible]:hover { opacity: 0.85; }
  </style>
</head>
<body>
  <div class="eda-report-wrap">
    ${body}
  </div>
  <script>
    (function () {
      'use strict';
      var CHARTS = ${cfgJSON};

      // ── Render all ECharts ────────────────────────────────────────────────
      document.querySelectorAll('[data-eda-chart]').forEach(function (div) {
        var id  = div.getAttribute('data-eda-chart');
        var opt = CHARTS[id];
        if (!opt) return;
        var h = parseInt(div.getAttribute('data-eda-h') || '180', 10);
        div.style.width  = '100%';
        div.style.height = h + 'px';
        // restore bubble symbolSize (functions can't be JSON-serialised)
        if (opt.series) opt.series.forEach(function (s) {
          if (s._bubbleSize) { delete s._bubbleSize; s.symbolSize = function (d) { return d[2]; }; }
        });
        try {
          echarts.init(div, null, { renderer: 'canvas' }).setOption(opt);
        } catch (e) {
          console.warn('EChart render error:', id, e);
        }
      });

      // ── Collapsible section headings ──────────────────────────────────────
      document.querySelectorAll('h2').forEach(function (h2) {
        var headRow = h2.parentElement;
        var secWrap = headRow && headRow.parentElement;
        if (!secWrap) return;
        var siblings = Array.from(secWrap.children);
        var headIdx  = siblings.indexOf(headRow);
        var content  = siblings.slice(headIdx + 1);
        if (!content.length) return;
        var collapsed = false;
        var pill = document.createElement('span');
        pill.textContent = ' \u25be';
        pill.style.cssText = 'font-size:10px;color:#627795;margin-left:5px;font-family:monospace;cursor:pointer;';
        h2.setAttribute('data-collapsible', '1');
        h2.style.cursor = 'pointer';
        h2.appendChild(pill);
        h2.addEventListener('click', function () {
          collapsed = !collapsed;
          content.forEach(function (c) { c.style.display = collapsed ? 'none' : ''; });
          pill.textContent = collapsed ? ' \u25b8' : ' \u25be';
          h2.style.opacity = collapsed ? '0.55' : '1';
        });
      });
    })();
  <\/script>
</body>
</html>`;

    dl(html, 'text/html', `${ds?.name || 'dataset'}-report.html`);
  };

  // Export MD
  const exportMD = () => {
    const totalRows = precomputed?.dataset?.rows || ds?.rows?.length || 0;
    const nullLookup = Object.fromEntries(nullCols.map(col => [col.h, col]));
    const dataPreviewHeaders = ds?.headers?.slice(0, 8) || [];
    const dataPreviewRows = ds?.rows?.slice(0, 5).map((row, idx) => ([
      idx + 1,
      ...dataPreviewHeaders.map(header => row?.[header] ?? ''),
    ])) || [];
    const numericStatRows = numCols
      .map(col => {
        const stats = allStats[col];
        if (!stats) return null;
        return [
          col,
          stats.count,
          mdNumber(stats.mean),
          mdNumber(stats.std),
          mdNumber(stats.min),
          mdNumber(stats.q1),
          mdNumber(stats.median),
          mdNumber(stats.q3),
          mdNumber(stats.max),
          mdNumber(stats.skew),
          stats.outlierCount ?? '-',
        ];
      })
      .filter(Boolean);
    const categoricalDistRows = catCols
      .map(col => {
        const profileEntry = precomputedDataMap?.[col];
        const counts = profileEntry?.valueCountsChart || (ds ? getValueCounts(ds.rows, col, 5) : []);
        const topValues = counts.slice(0, 5).map(item => {
          const pct = item.pct != null ? item.pct : totalRows ? +((item.count / totalRows) * 100).toFixed(1) : 0;
          return `${item.name}: ${item.count} (${pct}%)`;
        }).join('; ');
        return [
          col,
          profileEntry?.uniqueCount ?? types.find(type => type.col === col)?.uniqueCount ?? '-',
          profileEntry?.nullCount ?? nullLookup[col]?.n ?? 0,
          topValues || '-',
        ];
      })
      .filter(Boolean);
    const positiveCorrRows = posCorr.map(pair => [
      pair.a,
      pair.b,
      mdNumber(pair.r),
      mdCorrelationStrength(pair.r),
    ]);
    const negativeCorrRows = negCorr.map(pair => [
      pair.a,
      pair.b,
      mdNumber(pair.r),
      mdCorrelationStrength(pair.r),
    ]);
    const distributionChartRows = distributionCards.map(card => [
      card.chart?.title || card.id,
      mdChartType(card.chart?.type),
      mdChartFields(card.chart),
      card.chart?.subtitle || '-',
    ]);
    const visualizationRows = reportVizCards.map(card => [
      card.chart?.title || card.id,
      mdChartType(card.chart?.type),
      mdChartFields(card.chart),
      card.chart?.agg || '-',
      card.annotation?.text || card.annotation || '-',
    ]);
    const buildCategoryRows = (items = []) => items.map(item => {
      const count = item.count ?? item.value ?? item.n ?? 0;
      const pct = item.pct != null ? item.pct : totalRows ? +((count / totalRows) * 100).toFixed(1) : 0;
      return [item.name ?? item.label ?? item.v ?? '-', count, `${pct}%`];
    });
    const buildChartDataMarkdown = (card, fallbackData = null) => {
      const chart = card?.chart || {};
      const title = chart.title || card.id;
      const type = chart.type;
      const precomputedCardData = card?._cardData ?? fallbackData;
      const categoryData = (col, limit = 12) => {
        const profileEntry = precomputedDataMap?.[col];
        return profileEntry?.valueCountsChart || getValueCounts(ds.rows, col, limit);
      };

      if (type === 'hist') {
        const histData = precomputedDataMap?.[chart.col]?.histChart || getHistogram(ds.rows, chart.col, 20);
        return mdMiniHeading(title) + mdTable(['Bin', 'Count'], histData.map(bin => [bin.label, bin.count]));
      }

      if (['donut', 'pie', 'hbar', 'radial', 'treemap', 'funnel'].includes(type)) {
        const rows = buildCategoryRows(Array.isArray(precomputedCardData) && precomputedCardData.length ? precomputedCardData : categoryData(chart.col, type === 'hbar' ? 20 : 12));
        return mdMiniHeading(title) + mdTable(['Category', 'Count', 'Share'], rows);
      }

      if (type === 'num_overview') {
        const cols = chart.cols || numCols.slice(0, 8);
        const rows = cols.map(col => {
          const stats = allStats[col];
          return stats ? [col, mdNumber(stats.mean), mdNumber(stats.max)] : null;
        }).filter(Boolean);
        return mdMiniHeading(title) + mdTable(['Column', 'Mean', 'Max'], rows);
      }

      if (['groupby', 'bar', 'line', 'area'].includes(type)) {
        const aggRows = Array.isArray(precomputedCardData) && precomputedCardData.length
          ? precomputedCardData.map(point => [point.name, mdNumber(point.value)])
          : computeAggrData(ds.rows, chart.xCol || chart.catCol, chart.yCol || chart.numCol, chart.agg || 'mean').map(point => [point.name, mdNumber(point.value)]);
        return mdMiniHeading(title) + mdTable(['Group', 'Value'], aggRows);
      }

      if (type === 'timeseries') {
        const seriesRows = Array.isArray(precomputedCardData) && precomputedCardData.length
          ? precomputedCardData.map(point => [point[0], mdNumber(point[1])])
          : (() => {
              const raw = ds.rows
                .map(row => ({ date: row[chart.dateCol], value: parseFloat(row[chart.valueCol || chart.yCol]) }))
                .filter(point => point.date && Number.isFinite(point.value));
              const step = raw.length > 20 ? Math.ceil(raw.length / 20) : 1;
              return raw.filter((_, idx) => idx % step === 0).slice(0, 20).map(point => [point.date, mdNumber(point.value)]);
            })();
        return mdMiniHeading(title) + mdTable(['Date', 'Value'], seriesRows);
      }

      if (type === 'boxplot') {
        const stats = allStats[chart.col];
        if (!stats) return '';
        return mdMiniHeading(title) + mdTable(['Metric', 'Value'], [
          ['Min', mdNumber(stats.min)],
          ['Q1', mdNumber(stats.q1)],
          ['Median', mdNumber(stats.median)],
          ['Q3', mdNumber(stats.q3)],
          ['Max', mdNumber(stats.max)],
          ['Outliers', stats.outlierCount ?? '-'],
        ]);
      }

      if (type === 'multibox') {
        const rows = (chart.cols || []).map(col => {
          const stats = allStats[col];
          return stats ? [col, mdNumber(stats.q1), mdNumber(stats.median), mdNumber(stats.q3)] : null;
        }).filter(Boolean);
        return rows.length ? mdMiniHeading(title) + mdTable(['Column', 'Q1', 'Median', 'Q3'], rows) : '';
      }

      if (type === 'parallel') {
        const sampleRows = Array.isArray(precomputedCardData) && precomputedCardData.length
          ? precomputedCardData.slice(0, 8)
          : [];
        if (!sampleRows.length) return '';
        return mdMiniHeading(title) + mdTable(chart.cols || [], sampleRows);
      }

      return '';
    };
    const distributionDataSections = distributionCards.map(card => buildChartDataMarkdown(card)).filter(Boolean);
    const visualizationDataSections = reportVizCards.map(card => buildChartDataMarkdown(card, card._cardData)).filter(Boolean);

    let md=`# ${title}\n\n**Analysis Report** · ${new Date().toDateString()} · ${ds?.name || 'Dataset'}\n\n---\n\n`;
    if(has('data')&&ds){
      md+=`## Data\n\nFirst ${dataPreviewRows.length} rows preview (${dataPreviewHeaders.length} columns shown).\n\n`;
      md+=mdTable(['Row', ...dataPreviewHeaders], dataPreviewRows);
    }
    if(has('overview')&&ds)md+=`## Dataset Overview\n\n- File: \`${ds.name}\`\n- Rows: ${ds.rows.length.toLocaleString()}\n- Columns: ${ds.headers.length}\n- Numeric: ${numCols.length} | Categorical: ${catCols.length}\n\n`;
    if(has('distributions')){
      md+=`## Data Distributions\n\n`;
      if (numericStatRows.length) {
        md+=`### Numeric Columns\n\n`;
        md+=mdTable(['Column', 'Count', 'Mean', 'Std', 'Min', 'Q1', 'Median', 'Q3', 'Max', 'Skew', 'Outliers'], numericStatRows);
      }
      if (categoricalDistRows.length) {
        md+=`### Categorical Columns\n\n`;
        md+=mdTable(['Column', 'Unique', 'Nulls', 'Top Values'], categoricalDistRows);
      }
      if (distributionChartRows.length) {
        md+=`### Distribution Chart Inventory\n\n`;
        md+=mdTable(['Title', 'Type', 'Fields', 'Notes'], distributionChartRows);
      }
      if (distributionDataSections.length) {
        md+=mdSubheading('Distribution Chart Data');
        md+=distributionDataSections.join('');
      }
    }
    if(has('stats')&&numericStatRows.length){
      md+=`## Statistics\n\n`;
      md+=mdTable(['Column', 'Count', 'Mean', 'Std', 'Min', 'Median', 'Max', 'Skew'], numericStatRows.map(row => [row[0], row[1], row[2], row[3], row[4], row[6], row[8], row[9]]));
    }
    if(has('correlations')&&(positiveCorrRows.length||negativeCorrRows.length)){
      md+=`## Key Correlations\n\n`;
      if (positiveCorrRows.length) {
        md+=`### Positive Relationships\n\n`;
        md+=mdTable(['Column A', 'Column B', 'r', 'Strength'], positiveCorrRows);
      }
      if (negativeCorrRows.length) {
        md+=`### Negative Relationships\n\n`;
        md+=mdTable(['Column A', 'Column B', 'r', 'Strength'], negativeCorrRows);
      }
    }
    if(has('visualization')){
      md+=`## Visualizations\n\n`;
      md+=visualizationRows.length
        ? mdTable(['Title', 'Type', 'Fields', 'Aggregation', 'Annotation'], visualizationRows)
        : `No pinned or auto-generated visualization cards are currently included in the report.\n\n`;
      if (visualizationDataSections.length) {
        md+=mdSubheading('Visualization Data');
        md+=visualizationDataSections.join('');
      }
    }
    if(has('health')&&health){
      md+=`## Data Health\n\n`;
      md+=`- Null cells: ${health.totalNulls.toLocaleString()} (${health.nullPct}%)\n`;
      md+=`- Duplicate rows: ${health.dupCount.toLocaleString()}\n`;
      md+=`- Complete rows: ${health.completeRows.toLocaleString()} (${health.completeRowPct}%)\n`;
      md+=`- Skewed columns: ${health.skewed.length ? health.skewed.join(', ') : 'None'}\n`;
      md+=`- Constant columns: ${health.constants.length ? health.constants.join(', ') : 'None'}\n\n`;
    }
    if(has('quality')&&health){
      md+=`## Data Quality\n\n`;
      md+=`- Quality score: **${health.qualityScore}/100**\n`;
      qualityNotes.forEach(n=>{ md+=`- ${n.text}\n`; });
      md+='\n';
    }
    if(notes.length>0){md+=`## Research Notes\n\n`;notes.forEach(n=>{md+=`**[${n.tag.toUpperCase()}]** ${n.text}\n\n`;});}
    md+=`## Conclusion\n\nAnalysis of \`${ds?.name||'dataset'}\` complete.`;
    if(topCorr.length>0)md+=` Strongest relationship: **${topCorr[0].a} ↔ ${topCorr[0].b}** (r = ${topCorr[0].r.toFixed(3)}).`;
    dl(md,'text/markdown',`${ds?.name||'dataset'}-report.md`);
  };

  const dl=(content,type,name)=>{
    const a=Object.assign(document.createElement('a'),{href:URL.createObjectURL(new Blob([content],{type})),download:name});
    a.click();URL.revokeObjectURL(a.href);
  };

  if(!ds) return (
    <div style={{padding:48,textAlign:'center',color:C.t4}}>
      <div style={{fontSize:30,marginBottom:8,opacity:0.3}}>📄</div>
      <div style={{fontSize:13,color:C.t3}}>Upload data first to compose a report</div>
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>

      {/* ── Controls bar ─────────────────────────────────────────────── */}
      <div style={{display:readOnly?'none':'flex',gap:8,alignItems:'center',flexWrap:'wrap',padding:'10px 14px',background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4}}>
        {/* title */}
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <span style={{fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace'}}>TITLE:</span>
          <input value={title} onChange={e=>setTitle(e.target.value)} style={{background:C.deep,border:`1px solid ${C.b2}`,color:C.t1,padding:'4px 8px',fontSize:10,fontFamily:'JetBrains Mono,monospace',borderRadius:3,outline:'none',minWidth:300}}/>
        </div>
        {/* section toggles */}
        <div style={{display:'flex',gap:3,flexWrap:'wrap'}}>
          {FIXED_SECS.map(s=>(
            <button key={s.id} onClick={()=>toggleSec(s.id,s.required)} style={{
              padding:'3px 8px',border:`1px solid ${has(s.id)?C.cyan:C.b1}`,
              background:has(s.id)?C.cyan+'15':'transparent',color:has(s.id)?C.cyan:C.t4,
              fontFamily:'JetBrains Mono,monospace',fontSize:8,borderRadius:2,
              cursor:s.required?'default':'pointer',opacity:s.required?.6:1,
            }}>{has(s.id)?'✓':'+'}  {s.label}{s.required?' *':''}</button>
          ))}
        </div>
        <div style={{marginLeft:'auto',display:'flex',gap:6}}>
          {/* floating ADD NOTE button */}
          <button onClick={()=>setNoteModal(true)} style={{padding:'5px 12px',border:`1px solid ${C.gold}`,background:C.gold+'15',color:C.gold,fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,borderRadius:3,cursor:'pointer'}}>
            ✎ ADD NOTE
          </button>
        </div>
      </div>

      {/* ── Report canvas ─────────────────────────────────────────────── */}
      <div ref={reportRef} style={{background:C.surface,border:`1px solid ${C.b1}`,borderRadius:6,padding:'32px 36px',lineHeight:1.8}}>
        {/* header */}
        <h1 style={{fontFamily:'Syne,sans-serif',fontWeight:800,fontSize:24,color:C.t1,margin:'0 0 4px'}}>{title}</h1>
        <div style={{fontSize:9,color:C.t3,fontFamily:'JetBrains Mono,monospace',marginBottom:22}}>
          ANALYSIS REPORT · {new Date().toLocaleDateString('en',{year:'numeric',month:'long',day:'numeric'})}
          {dashboardCards?.length?` · ${dashboardCards.length} charts`:''}{notes.length?` · ${notes.length} notes`:''}
        </div>
        <div style={{height:1,background:`linear-gradient(90deg,${C.cyan}44 0%,transparent 80%)`,marginBottom:28}}/>

        <div data-noprint="true" style={{fontSize:8,color:C.t4,letterSpacing:'.1em',marginBottom:16,fontFamily:'JetBrains Mono,monospace',display:readOnly?'none':'flex',alignItems:'center',gap:8}}>
          USE ↑ / ↓ TO REORDER SECTIONS
          {notes.length>0&&<span style={{color:C.gold}}>· USE NOTE ↑ / ↓ TO MOVE NOTES ANYWHERE</span>}
        </div>

        {/* ── SECTIONS in order ── */}
        {secOrder.map(sid=>{
          if(!has(sid)) return null;
          const sProps={
            onRemove:()=>toggleSec(sid,false),
            required:FIXED_SECS.find(s=>s.id===sid)?.required,
            onMoveUp:()=>moveSection(sid,-1),
            onMoveDown:()=>moveSection(sid,1),
            canMoveUp:secOrder.indexOf(sid)>0,
            canMoveDown:secOrder.indexOf(sid)>=0 && secOrder.indexOf(sid)<secOrder.length-1,
            readOnly,
          };

          if(sid==='data') return (
            <React.Fragment key="data_block">
              <DragSection key="data" title="Data" color={C.violet} {...sProps}>
                <div style={{display:'flex',flexDirection:'column',gap:16}}>
                  <div>
                    <div style={{fontSize:10,color:C.t3,fontFamily:'JetBrains Mono,monospace',marginBottom:8}}>HEAD (First 5 Rows)</div>
                    <div style={{overflowX:'auto',border:`1px solid ${C.b1}`,borderRadius:3}}>
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:9}}>
                        <thead><tr style={{background:C.deep}}>
                          {ds.headers.slice(0,10).map(h=>(
                            <th key={h} style={{padding:'4px 6px',textAlign:'left',color:C.t3,borderBottom:`1px solid ${C.b0}`,fontFamily:'JetBrains Mono,monospace'}}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {(ds.sampleRows||ds.rows).slice(0,5).map((r,i)=>(
                            <tr key={i}>
                              {ds.headers.slice(0,10).map(h=>(
                                <td key={h} style={{padding:'3px 6px',borderBottom:`1px solid ${C.b0}`,color:C.t2,fontFamily:'JetBrains Mono,monospace'}}>{String(r[h] || '').slice(0,20)}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {ds.headers.length > 10 && <div style={{fontSize:8,color:C.t4,marginTop:4}}>Showing first 10 columns of {ds.headers.length} total</div>}
                  </div>
                  <div>
                    <div style={{fontSize:10,color:C.t3,fontFamily:'JetBrains Mono,monospace',marginBottom:8}}>TAIL (Last 5 Rows)</div>
                    <div style={{overflowX:'auto',border:`1px solid ${C.b1}`,borderRadius:3}}>
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:9}}>
                        <thead><tr style={{background:C.deep}}>
                          {ds.headers.slice(0,10).map(h=>(
                            <th key={h} style={{padding:'4px 6px',textAlign:'left',color:C.t3,borderBottom:`1px solid ${C.b0}`,fontFamily:'JetBrains Mono,monospace'}}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {(ds.sampleRows||ds.rows).slice(-5).map((r,i)=>(
                            <tr key={i}>
                              {ds.headers.slice(0,10).map(h=>(
                                <td key={h} style={{padding:'3px 6px',borderBottom:`1px solid ${C.b0}`,color:C.t2,fontFamily:'JetBrains Mono,monospace'}}>{String(r[h] || '').slice(0,20)}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {ds.headers.length > 10 && <div style={{fontSize:8,color:C.t4,marginTop:4}}>Showing first 10 columns of {ds.headers.length} total</div>}
                  </div>
                </div>
              </DragSection>
              {renderAnchoredNotes('data')}
            </React.Fragment>
          );

          if(sid==='overview') return (
            <React.Fragment key="overview_block">
              <DragSection key="overview" title="Dataset Overview" color={C.cyan} {...sProps}>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:14}}>
                  <MetricTile label="ROWS"        value={(precomputed?.dataset?.rows??ds.rows.length).toLocaleString()} color={C.cyan}/>
                  <MetricTile label="COLUMNS"     value={ds.headers.length}               color={C.green}/>
                  <MetricTile label="NUMERIC"     value={numCols.length}                  color={C.violet}/>
                  <MetricTile label="CATEGORICAL" value={catCols.length}                  color={C.gold}/>
                </div>
                <p style={{fontSize:12,color:C.t2,lineHeight:1.8,margin:0}}>
                  Dataset <strong style={{color:C.t1}}>{ds.name}</strong> contains <strong style={{color:C.cyan}}>{(precomputed?.dataset?.rows??ds.rows.length).toLocaleString()} rows</strong> and <strong style={{color:C.cyan}}>{ds.headers.length} columns</strong> — {numCols.length} numeric and {catCols.length} categorical.
                </p>
              </DragSection>
              {renderAnchoredNotes('overview')}
            </React.Fragment>
          );

          if(sid==='stats'&&numCols.length>0) return (
            <React.Fragment key="stats_block">
            <DragSection key="stats" title="Statistics" color={C.green} {...sProps}>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                  <thead><tr style={{background:C.deep}}>
                    {['COLUMN','COUNT','MEAN','STD','MIN','MEDIAN','MAX','SKEW','NULLS'].map(h=>(
                      <th key={h} style={{padding:'6px 10px',textAlign:'left',fontSize:9,color:C.t3,letterSpacing:'.1em',borderBottom:`1px solid ${C.b0}`}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {numCols.map(col=>{const s=allStats[col];if(!s)return null;return(
                      <tr key={col} style={{borderBottom:`1px solid ${C.b0}`}}>
                        <td style={{padding:'6px 10px',color:C.t1,fontFamily:'JetBrains Mono,monospace'}}>{col}</td>
                        {[s.count,s.mean,s.std,s.min,s.median,s.max,s.skew].map((v,i)=>(
                          <td key={i} style={{padding:'6px 10px',color:C.t2,fontFamily:'JetBrains Mono,monospace'}}>{v}</td>
                        ))}
                        <td style={{padding:'6px 10px',color:s.nulls>0?C.red:C.t4,fontFamily:'JetBrains Mono,monospace'}}>{s.nulls}</td>
                      </tr>
                    );})}
                  </tbody>
                </table>
              </div>
            </DragSection>
            {renderAnchoredNotes('stats')}
            </React.Fragment>
          );

          if(sid==='correlations'&&topCorr.length>0) return (
            <React.Fragment key="correlations_block">
            <DragSection key="correlations" title="Key Correlations" color={C.violet} {...sProps}>
              {/* Positive / Negative tabs */}
              <div data-noprint="true" style={{display:'flex',gap:4,marginBottom:14,alignItems:'center'}}>
                {[
                  ['positive', `▲ Positive (${posCorr.length})`],
                  ['negative', `▼ Negative (${negCorr.length})`],
                ].map(([tab, label]) => (
                  <button key={tab} onClick={()=>setCorrTab(tab)} style={{
                    padding:'4px 14px',border:`1px solid ${corrTab===tab?(tab==='positive'?C.green:C.red):C.b1}`,
                    background:corrTab===tab?(tab==='positive'?C.green+'18':C.red+'18'):'transparent',
                    color:corrTab===tab?(tab==='positive'?C.green:C.red):C.t3,
                    fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,borderRadius:2,cursor:'pointer',
                  }}>{label}</button>
                ))}
                <span style={{fontSize:8,color:C.t4,fontFamily:'JetBrains Mono,monospace',marginLeft:6}}>
                  {corrTab==='positive'?'r > 0 — columns that increase together':'r < 0 — columns that move inversely'}
                </span>
              </div>
              {/* All pairs rendered in DOM (tabs control visibility; export shows all) */}
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:16}}>
                {topCorr.slice(0,6).map((p,i)=>{
                  const isPos = p.r >= 0;
                  const visible = (corrTab==='positive' && isPos) || (corrTab==='negative' && !isPos);
                  return (
                    <div key={i} data-corr-tab={isPos?'positive':'negative'}
                      style={{display:visible?'block':'none',background:C.surface,border:`1px solid ${C.b1}`,borderRadius:4,padding:'12px'}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                        <div style={{fontSize:10,color:C.t1,fontFamily:'JetBrains Mono,monospace'}}>{p.a} ↔ {p.b}</div>
                        <div style={{fontSize:11,color:rColor(p.r),fontWeight:700,fontFamily:'JetBrains Mono,monospace'}}>r = {p.r.toFixed(3)}</div>
                      </div>
                      <div id={`corr-chart-${i}`} style={{height:120}}>
                        <EChart height={120} option={corrScatterOpts[i]} />
                      </div>
                    </div>
                  );
                })}
              </div>
              {(corrTab==='positive'?posCorr:negCorr).length===0&&(
                <div style={{padding:'24px',textAlign:'center',color:C.t4,fontSize:11,fontFamily:'JetBrains Mono,monospace'}}>
                  No {corrTab} correlations in the top {topCorr.length} pairs.
                </div>
              )}
            </DragSection>
            {renderAnchoredNotes('correlations')}
            </React.Fragment>
          );

          if(sid==='distributions') return (
            <React.Fragment key="distributions_block">
            <DragSection key="distributions" title="Data Distributions" color={C.cyan} {...sProps}>
              <div style={{marginBottom:10,fontSize:10,color:C.t3,lineHeight:1.7}}>
                Showing the full distribution set for the current dataset. Distribution-tab pins stay in this section, and pinned visualization charts are kept out so they do not replace the histogram run.
              </div>
              <DashboardChartsSection dashboardCards={distributionCards} rows={ds.rows} colorMap={colorMap} profile={profile} onRemoveChart={removeChartFromReport} precomputedData={precomputedDataMap}/>
            </DragSection>
            {renderAnchoredNotes('distributions')}
            </React.Fragment>
          );

          if(sid==='visualization') return (
            <React.Fragment key="visualization_block">
            <DragSection key="visualization" title="Visualizations" color={C.orange} {...sProps}>
              <DashboardChartsSection dashboardCards={reportVizCards} rows={ds.rows} colorMap={colorMap} profile={profile} onRemoveChart={removeChartFromReport} precomputedData={precomputedDataMap}/>
            </DragSection>
            {renderAnchoredNotes('visualization')}
            </React.Fragment>
          );

          if(sid==='health'&&health) return (
            <React.Fragment key="health_block">
            <DragSection key="health" title="Data Health" color={C.gold} {...sProps}>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:14}}>
                <MetricTile label="NULL CELLS" value={`${health.totalNulls.toLocaleString()} (${health.nullPct}%)`} color={health.nullPct>20?C.red:health.nullPct>5?C.gold:C.green}/>
                <MetricTile label="DUPLICATES" value={health.dupCount.toLocaleString()} color={health.dupCount>0?C.gold:C.green}/>
                <MetricTile label="COMPLETE ROWS" value={`${health.completeRows.toLocaleString()} (${health.completeRowPct}%)`} color={C.cyan}/>
                <MetricTile label="TOTAL CELLS" value={health.totalCells.toLocaleString()} color={C.violet}/>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div style={{background:C.raised,border:`1px solid ${C.b1}`,borderRadius:4,padding:'10px 12px'}}>
                  <div style={{fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.08em',marginBottom:6}}>STRUCTURAL FLAGS</div>
                  <div style={{display:'flex',flexDirection:'column',gap:4,fontSize:10.5,color:C.t2}}>
                    <div>Likely ID columns: <strong style={{color:C.t1}}>{health.idLike.length ? health.idLike.join(', ') : 'None'}</strong></div>
                    <div>Constant columns: <strong style={{color:health.constants.length?C.red:C.t1}}>{health.constants.length ? health.constants.join(', ') : 'None'}</strong></div>
                    <div>High-cardinality categoricals: <strong style={{color:health.highCard.length?C.gold:C.t1}}>{health.highCard.length ? health.highCard.join(', ') : 'None'}</strong></div>
                  </div>
                </div>

                <div style={{background:C.raised,border:`1px solid ${C.b1}`,borderRadius:4,padding:'10px 12px'}}>
                  <div style={{fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.08em',marginBottom:6}}>DISTRIBUTION HEALTH</div>
                  <div style={{display:'flex',flexDirection:'column',gap:4,fontSize:10.5,color:C.t2}}>
                    <div>Skewed columns (|skew| &gt; 1): <strong style={{color:health.skewed.length?C.gold:C.t1}}>{health.skewed.length ? health.skewed.slice(0,8).join(', ') : 'None'}</strong></div>
                    <div>Outlier-heavy columns (&gt;5%): <strong style={{color:health.outlierHeavy.length?C.red:C.t1}}>{health.outlierHeavy.length ? health.outlierHeavy.map(c=>`${c.col} (${c.pct}%)`).join(', ') : 'None'}</strong></div>
                    <div>Columns with nulls: <strong style={{color:nullCols.length?C.gold:C.t1}}>{nullCols.length}</strong> / {ds.headers.length}</div>
                  </div>
                </div>
              </div>

              {nullCols.length>0&&(
                <div style={{marginTop:12,overflowX:'auto',border:`1px solid ${C.b1}`,borderRadius:4}}>
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:10,minWidth:560}}>
                    <thead><tr style={{background:C.deep}}>
                      {['COLUMN','NULL COUNT','NULL %','STATUS'].map(h=>(
                        <th key={h} style={{padding:'7px 10px',textAlign:'left',fontSize:8,color:C.t4,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.07em',borderBottom:`1px solid ${C.b1}`}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {[...nullCols].sort((a,b)=>b.n-a.n).slice(0,15).map((row,idx)=>(
                        <tr key={row.h} style={{borderBottom:`1px solid ${C.b0}`,background:idx%2?`${C.raised}40`:'transparent'}}>
                          <td style={{padding:'6px 10px',color:C.t1,fontWeight:600}}>{row.h}</td>
                          <td style={{padding:'6px 10px',color:C.t2,fontFamily:'JetBrains Mono,monospace'}}>{row.n.toLocaleString()}</td>
                          <td style={{padding:'6px 10px',color:row.pct>20?C.red:row.pct>5?C.gold:C.t3,fontFamily:'JetBrains Mono,monospace'}}>{row.pct}%</td>
                          <td style={{padding:'6px 10px',color:row.pct>20?C.red:row.pct>5?C.gold:C.green,fontFamily:'JetBrains Mono,monospace'}}>{row.pct>20?'HIGH RISK':row.pct>5?'WATCH':'LOW'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </DragSection>
            {renderAnchoredNotes('health')}
            </React.Fragment>
          );

          if(sid==='quality'&&health) return (
            <React.Fragment key="quality_block">
            <DragSection key="quality" title="Data Quality" color={C.green} {...sProps}>
              <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:12,marginBottom:12}}>
                <div style={{background:C.raised,border:`1px solid ${C.b1}`,borderLeft:`3px solid ${health.qualityScore>=80?C.green:health.qualityScore>=60?C.gold:C.red}`,borderRadius:4,padding:'12px 14px'}}>
                  <div style={{fontSize:8,color:C.t4,letterSpacing:'.11em',fontFamily:'JetBrains Mono,monospace'}}>QUALITY SCORE</div>
                  <div style={{fontFamily:'Syne,sans-serif',fontWeight:900,fontSize:30,color:health.qualityScore>=80?C.green:health.qualityScore>=60?C.gold:C.red,lineHeight:1.1}}>{health.qualityScore}/100</div>
                  <div style={{fontSize:9,color:C.t3,marginTop:4}}>
                    {health.qualityScore>=80?'Production-ready quality for analytics workflows.' : health.qualityScore>=60?'Usable with targeted cleaning and validation.' : 'High-risk quality; cleaning required before analysis/modeling.'}
                  </div>
                </div>
                <div style={{background:C.raised,border:`1px solid ${C.b1}`,borderRadius:4,padding:'10px 12px'}}>
                  <div style={{fontSize:9,color:C.t4,fontFamily:'JetBrains Mono,monospace',letterSpacing:'.08em',marginBottom:6}}>AUTOMATED QUALITY FINDINGS</div>
                  <div style={{display:'flex',flexDirection:'column',gap:6}}>
                    {qualityNotes.map((n,idx)=>(
                      <div key={idx} style={{padding:'7px 9px',background:n.kind==='risk'?`${C.red}0f`:n.kind==='warn'?`${C.gold}14`:`${C.green}12`,border:`1px solid ${n.kind==='risk'?`${C.red}35`:n.kind==='warn'?`${C.gold}35`:`${C.green}35`}`,borderRadius:3,fontSize:10.5,color:C.t2,lineHeight:1.65}}>
                        {n.text}
                      </div>
                    ))}
                    {profile?.quality?.slice(0,8).map((ins,idx)=>(
                      <div key={`p_${idx}`} style={{padding:'7px 9px',background:`${ins.color}0f`,border:`1px solid ${ins.color}35`,borderRadius:3,fontSize:10.5,color:C.t2,lineHeight:1.65}}>
                        {ins.text}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
                <MetricTile label="MODEL READINESS" value={health.nullPct<10&&health.dupCount===0&&health.constants.length===0?'HIGH':'MEDIUM'} color={health.nullPct<10&&health.dupCount===0&&health.constants.length===0?C.green:C.gold} sub="Based on nulls, duplicates, and structural flags"/>
                <MetricTile label="ANALYTICS READINESS" value={health.qualityScore>=75?'HIGH':health.qualityScore>=55?'MEDIUM':'LOW'} color={health.qualityScore>=75?C.green:health.qualityScore>=55?C.gold:C.red} sub="Overall quality score and data completeness"/>
                <MetricTile label="RISK FACTORS" value={(health.constants.length + (health.highCard.length>0?1:0) + (health.skewed.length>0?1:0) + (health.outlierHeavy.length>0?1:0) + (health.nullPct>10?1:0)).toString()} color={C.violet} sub="Combined structural and statistical risk signals"/>
              </div>
            </DragSection>
            {renderAnchoredNotes('quality')}
            </React.Fragment>
          );

          if(sid==='conclusion') return (
            <React.Fragment key="conclusion_block">
            <DragSection key="conclusion" title="Conclusion" color={C.green} {...sProps}>
              <p style={{fontSize:12,color:C.t2,lineHeight:1.8,margin:'0 0 14px'}}>
                Analysis of <strong style={{color:C.t1}}>{ds.name}</strong> complete. {(precomputed?.dataset?.rows??ds.rows.length).toLocaleString()} records across {ds.headers.length} features — {numCols.length} numeric, {catCols.length} categorical.
                {topCorr.length>0&&<> Strongest relationship: <strong style={{color:rColor(topCorr[0].r)}}>{topCorr[0].a} ↔ {topCorr[0].b}</strong> (r = {topCorr[0].r.toFixed(3)}).</>}
                {nullCols.length>0&&<> {nullCols.length} column{nullCols.length!==1?'s':''} have missing values requiring attention.</>}
                {dashboardCards?.length>0&&<> {dashboardCards.length} chart{dashboardCards.length!==1?'s':''} highlighted in dashboard.</>}
              </p>
            </DragSection>
            {renderAnchoredNotes('conclusion')}
            </React.Fragment>
          );

          return null;
        })}

      </div>

      <div style={{display:'flex',justifyContent:'center',gap:8,flexWrap:'wrap',width:'100%'}}>
        <button onClick={exportMD} style={{padding:'5px 12px',border:`1px solid ${C.cyan}`,background:C.cyan+'15',color:C.cyan,fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,borderRadius:3,cursor:'pointer'}}>EXPORT MD REPORT</button>
        <button onClick={exportHTML} style={{padding:'5px 12px',border:`1px solid ${C.green}`,background:C.green+'15',color:C.green,fontFamily:'JetBrains Mono,monospace',fontSize:9,fontWeight:700,borderRadius:3,cursor:'pointer'}}>EXPORT HTML REPORT</button>
      </div>

      {/* Note add modal */}
      {!readOnly&&noteModal&&<NoteModal onSave={addNote} onClose={()=>setNoteModal(false)}/>}
    </div>
  );
}
